import React, { useEffect, useMemo, useState, useLayoutEffect, useRef } from "react";

import { debounce, isEqual } from "lodash";
import PropTypes from "prop-types";

import { isFunction } from "@cayuse/shared-react.utils.type-checks";

import { searchCodes, useCodeSearchNavigation } from "components/flowable/ClassificationCodes/helpers";
import SearchBar from "components/SearchBar";
import { useDictionary } from "hooks";

import styles from "./styles.css";


const CodeSearch = ({
	code,
	codeType,
	disabled,
	required,
	onChangeCode,
	toCodeOptions,
	onBlur,
	onFocus,
	className,
	setTouched,
	...rest
}) => {
	const prevCode = useRef(code);
	const [ isFetchingOptions, setIsFetchingOptions ] = useState(false);
	const [ options, setOptions ] = useState([]);
	const [ hasBlurred, setHasBlurred ] = useState(false);
	const { common: { searchPlaceholder }, errorMessages: { empty } } = useDictionary("classificationCodes");

	const {
		setIsOpen,
		text,
		preparedOptions,
		wrapperRef,
	} = useCodeSearchNavigation({ value: code, onChangeCode, options });

	useLayoutEffect(() => {
		// reset the state of the options when user clears the selected code and the dropdown is open
		setOptions(code ? toCodeOptions(options, false) : []);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ code ]);

	const errorMessage = useMemo(
		() => {
			if (required && hasBlurred && !code) {
				return empty;
			}

			return null;
		},
		[ code, empty, hasBlurred, required ]
	);

	const doSearch = useMemo(() => debounce(
		searchText => {
			if (!!searchText) {
				setIsFetchingOptions(true);

				searchCodes(codeType, searchText)
					.then(options => {
						setOptions(toCodeOptions(options));
						setIsFetchingOptions(false);
					});
			}
		},
		300,
	), [ codeType, toCodeOptions ]);

	useEffect(() => {
		return () => doSearch.cancel();
	}, [ doSearch ]);

	const handleBlur = (...args) => {
		setHasBlurred(true);
		if (isFunction(onBlur)) {
			onBlur(...args);
		}

		setIsOpen(false);
	};

	const handleFocus = (...args) => {
		setHasBlurred(false);
		if (isFunction(onFocus)) {
			onFocus(...args);
		}

		if (isEqual(toCodeOptions(options, false), preparedOptions)) {
			setOptions(preparedOptions);
		} else {
			setOptions(toCodeOptions(options, false));
		}
		setTouched(true);
		setIsOpen(true);
	};

	useEffect(() => {
		doSearch(code);
	}, []);

	useEffect(() => {
		// do search after component mounted and prev 'code' is empty string
		if (code && prevCode.current !== code) {
			prevCode.current = code;
			doSearch(code);
		}
	}, [ code ]);

	return (
		<div ref={wrapperRef}>
			<SearchBar
				className={`${styles.codeSearch} ${className || ""}`}
				loading={isFetchingOptions}
				options={preparedOptions}
				onChange={(e, { value }) => onChangeCode(value)}
				onSearchChange={(e, { searchQuery }) => doSearch(searchQuery)}
				value={code}
				disabled={disabled}
				onBlur={handleBlur}
				onFocus={handleFocus}
				selectOnBlur={false}
				data-required={required && !code}
				error={!!errorMessage}
				placeholder={searchPlaceholder}
				selectOnNavigation={false}
				text={text}
				{...rest}
			/>
			{!!errorMessage && <span className={styles.inputError}>{errorMessage}</span>}
		</div>
	);
};

CodeSearch.defaultProps = {
	code: "",
};

CodeSearch.propTypes = {
	code: PropTypes.string,
	codeType: PropTypes.oneOf([ "for", "herdc", "seo", "toa" ]),
	disabled: PropTypes.bool,
	required: PropTypes.bool,
	onChangeCode: PropTypes.func.isRequired,
	toCodeOptions: PropTypes.func.isRequired,
};

export default CodeSearch;
